export function deriveStatus(oh) {
  if (oh?.status === "Cancelled") return "Cancelled";
  // An explicit "done" counts as well as feedback. Completion used to be
  // inferred purely from feedback existing, which left an open house that
  // happened but produced nothing worth writing down stuck on "Scheduled".
  if (oh?.status === "Completed" || oh?.feedback) return "Completed";
  return "Scheduled";
}

// Marked done with nothing to report. Henry's call 2026-08-02: closed means
// closed — no recap to send, no feedback to add afterwards, no restarting it.
// The row goes quiet apart from Delete.
export function isClosedNoFeedback(oh) {
  return deriveStatus(oh) === "Completed" && !oh?.feedback;
}

export function sortOpenHouses(rows) {
  return [...rows].sort((a, b) => {
    const aIsScheduled = deriveStatus(a) === "Scheduled";
    const bIsScheduled = deriveStatus(b) === "Scheduled";
    if (aIsScheduled !== bIsScheduled) return aIsScheduled ? -1 : 1;
    if (aIsScheduled) return (a.date || "").localeCompare(b.date || "");
    return (b.date || "").localeCompare(a.date || "");
  });
}

// Pre-Market included so "sneak peek" open houses can be scheduled before
// the listing goes fully live (2026-07-27, Henry's call after a Pre-Market
// listing was missing from the schedule dropdown).
const ELIGIBLE_STAGES = new Set(["Pre-Market", "Coming Soon", "Active"]);

export function filterEligibleListings(deals) {
  return deals.filter(
    (d) => d?.dealtype === "Seller" && ELIGIBLE_STAGES.has(d?.dealStage)
  );
}

// Up For Grabs is stricter than the schedule dropdown: only listings a guest
// could actually walk through soon — Coming Soon or Active, and not deleted.
// Pre-Market stays OUT here (it flooded the picker with ~19 parked listings and
// soft-deleted husks — 39 options where only 16 were real; 2026-08-27, Henry:
// "should only be anything coming soon or active. i want to make it easier to
// find"). The schedule dropdown keeps Pre-Market for sneak peeks (his 7/27 call).
const UP_FOR_GRABS_STAGES = new Set(["Coming Soon", "Active"]);

export function filterUpForGrabsListings(deals) {
  return (deals || []).filter(
    (d) =>
      d?.dealtype === "Seller" &&
      UP_FOR_GRABS_STAGES.has(d?.dealStage) &&
      !(Number(d?.deletedAt) > 0),
  );
}

// A claimable slot must point at a house you can still hold an open house on.
// Without this, an Open opportunity outlived its listing: live data 2026-08-27
// had an open slot for a deal already Under Contract. Cancelled/Filled slots
// are excluded here too — callers show open, claimable slots only.
export function liveOpenOpportunities(opportunities, deals) {
  const byId = new Map(
    (deals || []).map((d) => [String(d?.createdat ?? d?.id), d]),
  );
  return (opportunities || []).filter((o) => {
    if (o?.status !== "Open") return false;
    const deal = byId.get(String(o?.dealId));
    return !!deal && filterUpForGrabsListings([deal]).length === 1;
  });
}

export function formatTimeRange(start, end) {
  if (!start || !end) return "—";
  return `${formatTime(start)} – ${formatTime(end)}`;
}

function formatTime(t) {
  const [hStr, mStr] = String(t).split(":");
  const h = Number(hStr);
  if (isNaN(h)) return t;
  const period = h >= 12 ? "pm" : "am";
  const h12 = ((h + 11) % 12) + 1;
  return `${h12}:${(mStr || "00").padStart(2, "0")}${period}`;
}

export function generateFeedbackToken() {
  const bytes = new Uint8Array(16); // 16 bytes = 128 bits of entropy
  window.crypto.getRandomValues(bytes);
  // Base64url-encode (URL-safe, no padding)
  let b64 = btoa(String.fromCharCode(...bytes));
  return b64.replace(/\+/g, "-").replace(/\//g, "_").replace(/=+$/, "");
}

// Which open houses a given user may see in their list. Admin/Supervisor see all.
// Everyone else sees ones they host OR that sit on a listing they own
// (listingAgentId), with a hostingAgentEmail fallback for OH docs created before
// the hosting/listing id fields existed.
export function visibleOpenHousesFor(openHouses, user) {
  if (!user) return [];
  if (user.role === "Admin" || user.role === "Supervisor") return openHouses || [];
  const uid = user.id;
  const email = (user.email || "").toLowerCase();
  return (openHouses || []).filter(
    (oh) =>
      oh.hostingAgentId === uid ||
      oh.listingAgentId === uid ||
      (oh.hostingAgentEmail && email && oh.hostingAgentEmail.toLowerCase() === email),
  );
}

// Decide whether to send the listing agent a copy of an open-house email and
// to whom. Pure + synchronous so it is easy to unit-test. Returns the recipient
// { email, name } to send to, or null to skip (no email, or the agent is already
// being emailed in this flow — deduped against excludeEmails).
export function chooseListingAgentRecipient({
  agentEmail,
  agentName,
  excludeEmails = [],
}) {
  const email = String(agentEmail || "").trim();
  if (!email) return null;
  const lower = email.toLowerCase();
  const excluded = excludeEmails
    .map((e) => String(e || "").trim().toLowerCase())
    .filter(Boolean);
  if (excluded.includes(lower)) return null;
  return { email, name: String(agentName || "").trim() };
}
